import { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DuplicateActionDialog } from './duplicate/DuplicateActionDialog';
import { toast } from 'sonner';
import {
  Search, Loader2, Users, Package, AlertTriangle, CheckCircle2,
  Copy, Phone, Mail, Fingerprint, Tag, Pill, GitMerge, Trash2
} from 'lucide-react';
import { keyed } from '../lib/listKeys';

const MATCH_LABELS = {
  exact_phone: { label: 'Same Phone', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
  exact_email: { label: 'Same Email', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
  similar_name: { label: 'Similar Name', color: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  exact_barcode: { label: 'Same Barcode', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
  same_generic: { label: 'Same Generic', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
};

const ACTION_STYLES = {
  merge: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  review: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  keep_both: 'bg-green-500/10 text-green-600 border-green-500/30',
};

export default function DuplicateDetectionPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanType, setScanType] = useState('all');

  // Action dialog
  const [actionDialog, setActionDialog] = useState(null); // { type:'product'|'customer', action:'merge'|'delete', items:[], groupIdx }
  const [selectedKeep, setSelectedKeep] = useState('');
  const [selectedDelete, setSelectedDelete] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  // Bulk selection
  const [selectedProductGroups, setSelectedProductGroups] = useState({});
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(null);

  const runScan = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/duplicate-detection', { scan_type: scanType });
      setReport(res.data);
      toast.success('Duplicate scan complete');
    } catch (err) {
      const { handleAIError } = await import('../lib/aiErrorHandler');
      handleAIError(err, 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  const openMergeDialog = (type, items, groupIdx) => {
    const idField = type === 'product' ? 'product_id' : 'patient_id';
    setSelectedKeep(items[0]?.[idField] || '');
    setSelectedDelete({});
    setActionDialog({ type, action: 'merge', items, groupIdx });
  };

  const openDeleteDialog = (type, items, groupIdx) => {
    const checks = {};
    const idField = type === 'product' ? 'product_id' : 'patient_id';
    items.forEach(it => { checks[it[idField]] = false; });
    setSelectedDelete(checks);
    setActionDialog({ type, action: 'delete', items, groupIdx });
  };

  const handleMerge = async () => {
    if (!actionDialog || !selectedKeep) return;
    setActionLoading(true);
    const idField = actionDialog.type === 'product' ? 'product_id' : 'patient_id';
    const mergeIds = actionDialog.items.filter(it => it[idField] !== selectedKeep).map(it => it[idField]);
    const endpoint = actionDialog.type === 'product' ? '/ai/duplicate-detection/merge-products' : '/ai/duplicate-detection/merge-customers';
    try {
      const res = await api.post(endpoint, { keep_id: selectedKeep, merge_ids: mergeIds });
      toast.success(res.data.message);
      removeGroup(actionDialog.type, actionDialog.groupIdx);
      setActionDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    if (!actionDialog) return;
    const idField = actionDialog.type === 'product' ? 'product_id' : 'patient_id';
    const deleteIds = Object.entries(selectedDelete).filter(([, v]) => v).map(([k]) => k);
    if (deleteIds.length === 0) { toast.error('Select items to delete'); return; }
    if (deleteIds.length === actionDialog.items.length) { toast.error('Cannot delete all items — keep at least one'); return; }
    setActionLoading(true);
    const endpoint = actionDialog.type === 'product' ? '/ai/duplicate-detection/delete-products' : '/ai/duplicate-detection/delete-customers';
    try {
      const res = await api.post(endpoint, { delete_ids: deleteIds });
      toast.success(res.data.message);
      // If some remain, update group; if only 1 remains, remove group
      const remaining = actionDialog.items.filter(it => !deleteIds.includes(it[idField]));
      if (remaining.length <= 1) {
        removeGroup(actionDialog.type, actionDialog.groupIdx);
      } else {
        updateGroup(actionDialog.type, actionDialog.groupIdx, remaining);
      }
      setActionDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally { setActionLoading(false); }
  };

  const removeGroup = (type, idx) => {
    if (!report) return;
    setReport(prev => {
      const key = type === 'product' ? 'product_duplicates' : 'customer_duplicates';
      const updated = [...(prev[key] || [])];
      updated.splice(idx, 1);
      return { ...prev, [key]: updated };
    });
  };

  const updateGroup = (type, idx, remainingItems) => {
    if (!report) return;
    setReport(prev => {
      const key = type === 'product' ? 'product_duplicates' : 'customer_duplicates';
      const itemsKey = type === 'product' ? 'products' : 'patients';
      const updated = [...(prev[key] || [])];
      updated[idx] = { ...updated[idx], [itemsKey]: remainingItems };
      return { ...prev, [key]: updated };
    });
  };

  // Bulk actions
  const selectedProdCount = Object.values(selectedProductGroups).filter(Boolean).length;
  const selectedCustCount = Object.values(selectedCustomerGroups).filter(Boolean).length;

  const toggleAllProducts = () => {
    const allSelected = selectedProdCount === (report?.product_duplicates?.length || 0);
    const next = {};
    if (!allSelected) (report?.product_duplicates || []).forEach((_, i) => { next[i] = true; });
    setSelectedProductGroups(next);
  };

  const toggleAllCustomers = () => {
    const allSelected = selectedCustCount === (report?.customer_duplicates?.length || 0);
    const next = {};
    if (!allSelected) (report?.customer_duplicates || []).forEach((_, i) => { next[i] = true; });
    setSelectedCustomerGroups(next);
  };

  const handleBulkDelete = async (type) => {
    setBulkLoading(true);
    const groups = type === 'product' ? report?.product_duplicates : report?.customer_duplicates;
    const selected = type === 'product' ? selectedProductGroups : selectedCustomerGroups;
    const idField = type === 'product' ? 'product_id' : 'patient_id';
    const itemsKey = type === 'product' ? 'products' : 'patients';
    const endpoint = type === 'product' ? '/ai/duplicate-detection/delete-products' : '/ai/duplicate-detection/delete-customers';

    let totalDeleted = 0;
    let totalFailed = 0;

    for (const [idx, isSelected] of Object.entries(selected)) {
      if (!isSelected) continue;
      const group = groups?.[parseInt(idx)];
      if (!group) continue;
      const items = group[itemsKey] || [];
      if (items.length < 2) continue;
      // Keep the first item, delete the rest
      const deleteIds = items.slice(1).map(it => it[idField]);
      try {
        await api.post(endpoint, { delete_ids: deleteIds });
        totalDeleted += deleteIds.length;
      } catch {
        totalFailed += deleteIds.length;
      }
    }

    // Remove processed groups from state
    const key = type === 'product' ? 'product_duplicates' : 'customer_duplicates';
    setReport(prev => {
      const updated = (prev[key] || []).filter((_, i) => !selected[i]);
      return { ...prev, [key]: updated };
    });

    if (type === 'product') setSelectedProductGroups({});
    else setSelectedCustomerGroups({});

    setBulkLoading(false);
    setBulkConfirmOpen(null);
    toast.success(`Deleted ${totalDeleted} duplicate ${type}(s)${totalFailed ? `. ${totalFailed} failed.` : ''}`);
  };

  const totalGroups = (report?.customer_duplicates?.length || 0) + (report?.product_duplicates?.length || 0);
  const trueDupes = report?.ai_analysis?.true_duplicates?.length || 0;

  const renderProductGroup = (group, i) => {
    const ml = MATCH_LABELS[group.match_type] || { label: group.match_type, color: '' };
    const aiMatch = report.ai_analysis?.true_duplicates?.find(td =>
      td.type === 'product' && group.products.some(p => td.ids?.includes(p.product_id))
    );
    const isFalsePos = report.ai_analysis?.false_positives?.find(fp =>
      fp.type === 'product' && group.products.some(p => fp.ids?.includes(p.product_id))
    );
    return (
      <div key={i} className={`p-3 border rounded-lg space-y-2 ${isFalsePos ? 'opacity-60' : ''} ${selectedProductGroups[i] ? 'ring-2 ring-primary/40' : ''}`} data-testid={`product-dupe-${i}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <input type="checkbox" checked={!!selectedProductGroups[i]} onChange={() => setSelectedProductGroups(prev => ({ ...prev, [i]: !prev[i] }))}
              className="h-4 w-4 accent-primary rounded shrink-0" data-testid={`select-product-group-${i}`} />
            <Badge variant="outline" className={`text-[9px] ${ml.color}`}>{ml.label}</Badge>
            {group.barcode && <span className="text-xs text-muted-foreground flex items-center gap-1"><Fingerprint className="h-3 w-3" />{group.barcode}</span>}
            {group.generic_name && <span className="text-xs text-muted-foreground flex items-center gap-1"><Pill className="h-3 w-3" />{group.generic_name}</span>}
            {aiMatch && <Badge variant="outline" className={`text-[9px] ${ACTION_STYLES[aiMatch.recommended_action] || ''}`}>{aiMatch.recommended_action}</Badge>}
            {isFalsePos && <Badge variant="outline" className="text-[9px] bg-green-500/10 text-green-600 border-green-500/30">Not a duplicate</Badge>}
          </div>
          {!isFalsePos && (
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-primary" onClick={() => openMergeDialog('product', group.products, i)} data-testid={`merge-products-${i}`}>
                <GitMerge className="h-3 w-3" /> Merge
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-destructive" onClick={() => openDeleteDialog('product', group.products, i)} data-testid={`delete-products-${i}`}>
                <Trash2 className="h-3 w-3" /> Delete
              </Button>
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {keyed(group.products, p => p.product_id || p.name).map(({ item: p, key }) => (
            <div key={key} className="p-2 bg-muted/50 rounded text-sm">
              <p className="font-medium">{p.name}</p>
              <div className="text-xs text-muted-foreground space-y-0.5 mt-0.5">
                {p.generic_name && <p className="flex items-center gap-1"><Pill className="h-3 w-3" />{p.generic_name}</p>}
                {p.manufacturer && <p className="flex items-center gap-1"><Tag className="h-3 w-3" />{p.manufacturer}</p>}
                {p.unit_price !== undefined && <p>Price: {p.unit_price}</p>}
                {p.stock_quantity !== undefined && <p>Stock: {p.stock_quantity}</p>}
              </div>
              <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{p.product_id}</p>
            </div>
          ))}
        </div>
        {(aiMatch?.reason || isFalsePos?.reason) && <p className="text-xs text-muted-foreground italic">{aiMatch?.reason || isFalsePos?.reason}</p>}
      </div>
    );
  };

  const renderCustomerGroup = (group, i) => {
    const ml = MATCH_LABELS[group.match_type] || { label: group.match_type, color: '' };
    const aiMatch = report.ai_analysis?.true_duplicates?.find(td =>
      td.type === 'customer' && group.patients.some(p => td.ids?.includes(p.patient_id))
    );
    return (
      <div key={i} className={`p-3 border rounded-lg space-y-2 ${selectedCustomerGroups[i] ? 'ring-2 ring-primary/40' : ''}`} data-testid={`customer-dupe-${i}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <input type="checkbox" checked={!!selectedCustomerGroups[i]} onChange={() => setSelectedCustomerGroups(prev => ({ ...prev, [i]: !prev[i] }))}
              className="h-4 w-4 accent-primary rounded shrink-0" data-testid={`select-customer-group-${i}`} />
            <Badge variant="outline" className={`text-[9px] ${ml.color}`}>{ml.label}</Badge>
            {group.phone && <span className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{group.phone}</span>}
            {group.email && <span className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{group.email}</span>}
            {aiMatch && <Badge variant="outline" className={`text-[9px] ${ACTION_STYLES[aiMatch.recommended_action] || ''}`}>{aiMatch.recommended_action}</Badge>}
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-primary" onClick={() => openMergeDialog('customer', group.patients, i)} data-testid={`merge-customers-${i}`}>
              <GitMerge className="h-3 w-3" /> Merge
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-destructive" onClick={() => openDeleteDialog('customer', group.patients, i)} data-testid={`delete-customers-${i}`}>
              <Trash2 className="h-3 w-3" /> Delete
            </Button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {keyed(group.patients, p => p.patient_id || p.customer_id || `${p.name}-${p.phone || ''}`).map(({ item: p, key }) => (
            <div key={key} className="p-2 bg-muted/50 rounded text-sm">
              <p className="font-medium">{p.name}</p>
              <p className="text-xs text-muted-foreground">
                {p.phone && <><Phone className="inline h-3 w-3 mr-1" />{p.phone}</>}
                {p.email && <><Mail className="inline h-3 w-3 mx-1" />{p.email}</>}
              </p>
              <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{p.patient_id}</p>
            </div>
          ))}
        </div>
        {aiMatch?.reason && <p className="text-xs text-muted-foreground italic">{aiMatch.reason}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="duplicate-detection-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Copy className="h-6 w-6 text-orange-500" /> Duplicate Detection
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">AI-powered detection of duplicate customers and products</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-end gap-3">
            <div className="space-y-1.5 flex-1">
              <Label className="text-xs">Scan Type</Label>
              <Select value={scanType} onValueChange={setScanType}>
                <SelectTrigger data-testid="scan-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Customers + Products)</SelectItem>
                  <SelectItem value="customers">Customers Only</SelectItem>
                  <SelectItem value="products">Products Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={runScan} disabled={loading} className="gap-2 h-10" data-testid="run-scan-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {loading ? 'Scanning...' : 'Run Scan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!report && !loading && (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Copy className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <h3 className="text-lg font-semibold">No Scan Run Yet</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Select a scan type and click "Run Scan" to check for duplicate entries.</p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="py-16 text-center">
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
            <h3 className="text-lg font-semibold">Scanning Database...</h3>
            <p className="text-sm text-muted-foreground mt-1">Comparing records and running AI analysis.</p>
          </CardContent>
        </Card>
      )}

      {report && !loading && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                <p className="text-2xl font-bold">{report.summary?.total_customers ?? '-'}</p>
                <p className="text-xs text-muted-foreground">Customers</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Package className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                <p className="text-2xl font-bold">{report.summary?.total_products ?? '-'}</p>
                <p className="text-xs text-muted-foreground">Products</p>
              </CardContent>
            </Card>
            <Card className={totalGroups > 0 ? 'border-orange-500/30 bg-orange-500/5' : 'border-green-500/30 bg-green-500/5'}>
              <CardContent className="p-4 text-center">
                {totalGroups > 0 ? <AlertTriangle className="h-6 w-6 mx-auto mb-1 text-orange-500" /> : <CheckCircle2 className="h-6 w-6 mx-auto mb-1 text-green-500" />}
                <p className="text-2xl font-bold">{totalGroups}</p>
                <p className="text-xs text-muted-foreground">Duplicate Groups</p>
              </CardContent>
            </Card>
            <Card className={trueDupes > 0 ? 'border-red-500/30 bg-red-500/5' : ''}>
              <CardContent className="p-4 text-center">
                <GitMerge className="h-6 w-6 mx-auto mb-1 text-red-500" />
                <p className="text-2xl font-bold">{trueDupes}</p>
                <p className="text-xs text-muted-foreground">AI-Confirmed</p>
              </CardContent>
            </Card>
          </div>

          {report.customer_duplicates?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4 text-orange-500" /> Customer Duplicates ({report.customer_duplicates.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={toggleAllCustomers} data-testid="select-all-customers">
                      {selectedCustCount === report.customer_duplicates.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedCustCount > 0 && (
                      <Button size="sm" variant="destructive" className="h-7 text-xs gap-1" onClick={() => setBulkConfirmOpen('customers')} disabled={bulkLoading} data-testid="bulk-delete-customers">
                        <Trash2 className="h-3 w-3" /> Delete {selectedCustCount} Group{selectedCustCount > 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {report.customer_duplicates.map((group, i) => renderCustomerGroup(group, i))}
              </CardContent>
            </Card>
          )}

          {report.product_duplicates?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="h-4 w-4 text-orange-500" /> Product Duplicates ({report.product_duplicates.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={toggleAllProducts} data-testid="select-all-products">
                      {selectedProdCount === report.product_duplicates.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedProdCount > 0 && (
                      <Button size="sm" variant="destructive" className="h-7 text-xs gap-1" onClick={() => setBulkConfirmOpen('products')} disabled={bulkLoading} data-testid="bulk-delete-products">
                        <Trash2 className="h-3 w-3" /> Delete {selectedProdCount} Group{selectedProdCount > 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>
                </div>
                {selectedProdCount > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">Bulk delete keeps the first product in each group and removes the rest.</p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {report.product_duplicates.map((group, i) => renderProductGroup(group, i))}
              </CardContent>
            </Card>
          )}

          {totalGroups === 0 && (
            <Card className="border-green-500/30">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                <h3 className="text-lg font-semibold text-green-600">No Duplicates Found</h3>
                <p className="text-sm text-muted-foreground mt-1">Your database appears clean.</p>
              </CardContent>
            </Card>
          )}

          {report.ai_analysis && (
            <div className="grid lg:grid-cols-2 gap-4">
              {report.ai_analysis.merge_recommendations?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><GitMerge className="h-4 w-4 text-primary" /> Merge Recommendations</CardTitle></CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {keyed(report.ai_analysis.merge_recommendations).map(({ item: r, key }, i) => (
                        <li key={key} className="flex items-start gap-2 text-sm">
                          <span className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>{r}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              {report.ai_analysis.data_quality_notes?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Data Quality Notes</CardTitle></CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {keyed(report.ai_analysis.data_quality_notes).map(({ item: n, key }) => (
                        <li key={key} className="text-sm flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />{n}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <p className="text-[10px] text-muted-foreground text-right">Scanned: {new Date(report.scanned_at).toLocaleString()} by {report.scanned_by}</p>
        </>
      )}

      {/* Merge / Delete Dialog */}
      <DuplicateActionDialog
        actionDialog={actionDialog}
        onClose={() => setActionDialog(null)}
        selectedKeep={selectedKeep}
        setSelectedKeep={setSelectedKeep}
        selectedDelete={selectedDelete}
        setSelectedDelete={setSelectedDelete}
        actionLoading={actionLoading}
        onMerge={handleMerge}
        onDelete={handleDelete}
      />

      {/* Bulk Delete Confirmation */}
      <Dialog open={!!bulkConfirmOpen} onOpenChange={() => setBulkConfirmOpen(null)}>
        <DialogContent className="max-w-sm" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" /> Bulk Delete Duplicates
            </DialogTitle>
            <DialogDescription>
              {bulkConfirmOpen === 'products'
                ? `This will delete duplicates from ${selectedProdCount} product group(s). The first product in each group will be kept, all others removed.`
                : `This will delete duplicates from ${selectedCustCount} customer group(s). The first customer in each group will be kept, all others removed.`}
              {' '}This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfirmOpen(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleBulkDelete(bulkConfirmOpen === 'products' ? 'product' : 'customer')} disabled={bulkLoading} className="gap-1.5" data-testid="confirm-bulk-delete-btn">
              {bulkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete All Duplicates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
